import { LocaleType } from '../locale';
import { LanguageCode } from '../types';

type LocaleResource = Partial<LanguageKeyMap> | undefined;

export type LocaleResourceMap = Partial<Record<LocaleType, Partial<LanguageKeyMap>>>;

type TranslationTexts = LanguageKeyMap;

const emptyResource: Partial<TranslationTexts> = {};

const baseResourceMap: Record<LocaleType, Partial<TranslationTexts>> = {
  zh: emptyResource,
  en: emptyResource,
  pt: emptyResource,
  ja: emptyResource,
  de: emptyResource,
  es: emptyResource,
  kor: emptyResource,
  th: emptyResource,
};

/**
 * 清理宿主或远程传入的翻译资源，避免 undefined/null 覆盖已有有效文案。
 */
function normalizeLocaleResource(locale: LocaleResource): Partial<LanguageKeyMap> {
  if (!locale) return {};

  return Object.fromEntries(
    Object.entries(locale).filter(([, value]) => value !== undefined && value !== null),
  ) as Partial<LanguageKeyMap>;
}

/**
 * 包一层安全读取，防止远程资源未加载或缺 key 时在业务回调里触发 undefined.replace。
 */
function createSafeTranslationTexts(texts: Partial<TranslationTexts>): TranslationTexts {
  return new Proxy(texts, {
    get(target, property) {
      const value = Reflect.get(target, property);

      return value === undefined || value === null ? '' : value;
    },
  }) as TranslationTexts;
}

/**
 * 创建多语言取文案方法，运行时语言包和宿主覆盖只接收有效值。
 */
export function createSmartcatTranslation({ getReactiveLang, locales }: { getReactiveLang(): LocaleType;locales?: LocaleResourceMap; }) {
  const normalizedLocales = {
    zh: normalizeLocaleResource(locales?.zh),
    en: normalizeLocaleResource(locales?.en),
    pt: normalizeLocaleResource(locales?.pt),
    ja: normalizeLocaleResource(locales?.ja),
    de: normalizeLocaleResource(locales?.de),
    es: normalizeLocaleResource(locales?.es),
    kor: normalizeLocaleResource(locales?.kor),
    th: normalizeLocaleResource(locales?.th),
  };
  // 合并宿主传入的资源
  const resourceMap = {
    [LanguageCode.zhHans]: { ...baseResourceMap.zh, ...normalizedLocales.zh },
    [LanguageCode.zhHant]: { ...baseResourceMap.en, ...normalizedLocales.zh },
    [LanguageCode.zh]: { ...baseResourceMap.zh, ...normalizedLocales.zh },
    [LanguageCode.en]: { ...baseResourceMap.en, ...normalizedLocales.en },
    [LanguageCode.pt]: { ...baseResourceMap.pt, ...normalizedLocales.pt },
    [LanguageCode.ja]: { ...baseResourceMap.ja, ...normalizedLocales.ja },
    [LanguageCode.de]: { ...baseResourceMap.de, ...normalizedLocales.de },
    [LanguageCode.es]: { ...baseResourceMap.es, ...normalizedLocales.es },
    [LanguageCode.kor]: { ...baseResourceMap.kor, ...normalizedLocales.kor },
    [LanguageCode.th]: { ...baseResourceMap.th, ...normalizedLocales.th },
  };
  return (fn: (text: TranslationTexts) => string, languageCode?: LanguageCode) => {
    // Ensure proper type for resource selection using LanguageCode
    const lang = languageCode || getReactiveLang();
    // Explicitly narrow type to avoid undefineds on translation keys
    const langKey = lang as keyof typeof resourceMap;
    // 语言资源运行时从 CDN 加载，加载前通过 Proxy 兜底空字符串，避免未命中 key 影响渲染。
    const texts: Partial<TranslationTexts> = { ...resourceMap.en, ...resourceMap[langKey] };
    return fn(createSafeTranslationTexts(texts));
  };
}

export type SmartcatTranslationText = (fn: (text: TranslationTexts) => string, languageCode?: LanguageCode) => string;

export type LanguageKeyMap = Record<string, string>;
